import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { User } from "@src/model/user";
import { useUserAPI } from "@src/api/user.api";
import { useAuth0 } from "@auth0/auth0-react";

type Output = {
    id: string;
    assetType: "video" | string;
    permalink: string;
    thumbnailImage?: string | undefined;
    _origin?: "generated" | "uploadedAdmin";
};

export interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    outputs: Output[];
}

type BoardDTO = {
    id: string | null;
    name: string;
};

type AdminUploadedVideoDTO = {
    generativeOutputId: string;
    permalink: string;
    thumbnailImage?: string | null;
};

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, outputs }) => {
    const { getUsers } = useUserAPI();
    const { getAccessTokenSilently } = useAuth0();

    const [searchQuery, setSearchQuery] = useState("");
    const [users, setUsers] = useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadedOutputs, setUploadedOutputs] = useState<Output[]>([]);
    const [stagedFiles, setStagedFiles] = useState<File[]>([]);

    const [sharePending, setSharePending] = useState(false);
    const [shareError, setShareError] = useState<string | null>(null);

    const [selectedOutputIds, setSelectedOutputIds] = useState<Set<string>>(new Set());

    const inputRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const dropRef = useRef<HTMLDivElement | null>(null);
    const dragCounterRef = useRef(0);
    const [dragActive, setDragActive] = useState(false);

    const reqIdRef = useRef(0);

    const getUsersRef = useRef(getUsers);
    useEffect(() => {
        getUsersRef.current = getUsers;
    }, [getUsers]);

    const baseApi = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

    const authedFetchJson = useCallback(
        async (url: string, init: RequestInit = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUD, scope: "read:current_user" },
            });
            const headers: HeadersInit = { ...(init.headers || {}), authorization: `Bearer ${token}` };
            const res = await fetch(url, { ...init, headers });
            if (res.status === 200) {
                const ct = res.headers.get("content-type") || "";
                return ct.includes("application/json") ? res.json() : res.text();
            }
            if (res.status === 401) throw new Error("User is not authenticated");
            let message = res.statusText;
            try {
                const j = await res.json();
                message = j?.message || JSON.stringify(j);
            } catch {}
            throw new Error(message);
        },
        [getAccessTokenSilently]
    );

    const uploadVideoForUser = useCallback(
        async (userId: string, file: File): Promise<AdminUploadedVideoDTO> => {
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUD, scope: "read:current_user" },
            });
            const data = new FormData();
            data.append("file", file);
            const res = await fetch(
                `${baseApi}/admin/uploads/video?userId=${encodeURIComponent(userId)}&dryRun=true`,
                { method: "POST", headers: { authorization: `Bearer ${token}` }, body: data }
            );
            if (res.status !== 200) {
                let message = res.statusText;
                try {
                    const j = await res.json();
                    message = j?.message || JSON.stringify(j);
                } catch {}
                throw new Error(message);
            }
            return res.json();
        },
        [baseApi, getAccessTokenSilently]
    );

    const addOutputsToSharedBoard = useCallback(
        async (userId: string, generativeOutputIds: string[]): Promise<BoardDTO> => {
            const body = JSON.stringify({ generativeOutputIds });
            return authedFetchJson(`${baseApi}/admin/boards/shared/${encodeURIComponent(userId)}/outputs`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body,
            }) as Promise<BoardDTO>;
        },
        [authedFetchJson, baseApi]
    );

    useEffect(() => {
        if (!isOpen) return;
        setSearchQuery("");
        setUsers([]);
        setSelectedUser(null);
        setLoadingUsers(false);
        setUsersError(null);
        setUploadedOutputs([]);
        setUploading(false);
        setUploadError(null);
        setSharePending(false);
        setShareError(null);
        setSelectedOutputIds(new Set());
        setStagedFiles([]);
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [isOpen]);

    // Debounced user search
    useEffect(() => {
        if (!isOpen) return;
        const q = searchQuery.trim();
        if (q.length === 0) {
            setUsers([]);
            setUsersError(null);
            setLoadingUsers(false);
            return;
        }
        const myReqId = ++reqIdRef.current;
        const timer = window.setTimeout(async () => {
            setLoadingUsers(true);
            setUsersError(null);
            try {
                const results = await getUsersRef.current(q, 20, 0);
                if (reqIdRef.current === myReqId) setUsers(results);
            } catch (e: any) {
                if (reqIdRef.current === myReqId) {
                    setUsers([]);
                    setUsersError(String(e?.message || e));
                }
            } finally {
                if (reqIdRef.current === myReqId) setLoadingUsers(false);
            }
        }, 250);
        return () => window.clearTimeout(timer);
    }, [searchQuery, isOpen]);

    const onPickUser = useCallback((u: User) => {
        setSelectedUser(u);
        setSearchQuery(u.email || "");
    }, []);

    const generatedVideos = useMemo(
        () => outputs.filter((o) => o.assetType === "video").map((o) => ({ ...o, _origin: "generated" as const })),
        [outputs]
    );

    const allVideos = useMemo(() => [...uploadedOutputs, ...generatedVideos], [uploadedOutputs, generatedVideos]);

    const doUploadForFiles = useCallback(
        async (videoFiles: File[]) => {
            if (!selectedUser?.id || videoFiles.length === 0) return;
            setUploadError(null);
            setUploading(true);
            try {
                const created: Output[] = [];
                for (const file of videoFiles) {
                    const dto = await uploadVideoForUser(String(selectedUser.id), file);
                    if (dto?.generativeOutputId && dto?.permalink) {
                        created.push({
                            id: dto.generativeOutputId,
                            assetType: "video",
                            permalink: dto.permalink,
                            thumbnailImage: dto.thumbnailImage ?? undefined,
                            _origin: "uploadedAdmin",
                        });
                    }
                }
                if (created.length) {
                    setUploadedOutputs((prev) => [...created, ...prev]);
                    setSelectedOutputIds((prev) => {
                        const next = new Set(prev);
                        created.forEach((c) => next.add(c.id));
                        return next;
                    });
                }
            } catch (e: any) {
                setUploadError(String(e?.message || e));
            } finally {
                setUploading(false);
            }
        },
        [selectedUser, uploadVideoForUser]
    );

    const stageOrUploadFiles = useCallback(
        (filesList: FileList | File[] | null) => {
            if (!filesList || (Array.isArray(filesList) ? filesList.length === 0 : filesList.length === 0)) return;
            const files = Array.isArray(filesList) ? filesList : Array.from(filesList);
            // Accept only .mp4/.mov by extension (covers missing MIME cases)
            const videoFiles = files.filter((f) => {
                const name = (f.name || "").toLowerCase();
                const ext = name.slice(name.lastIndexOf(".") + 1);
                const byMime = f.type?.startsWith("video/");
                const byExt = ext === "mp4" || ext === "mov";
                return byExt || (byMime && byExt);
            });
            if (videoFiles.length === 0) {
                setUploadError("Choose an .mp4 or .mov video");
                return;
            }
            if (!selectedUser?.id) {
                // Stage locally; we’ll auto-upload after a user is picked
                setStagedFiles((prev) => [...videoFiles, ...prev]);
                return;
            }
            void doUploadForFiles(videoFiles);
        },
        [selectedUser, doUploadForFiles]
    );

    // ✅ FIX: clone FileList before clearing input
    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files ? Array.from(e.target.files) : [];
        stageOrUploadFiles(selected);
        e.target.value = ""; // allow re-selecting same file later
    };

    // DnD wiring
    useEffect(() => {
        const zone = dropRef.current;
        if (!zone) return;
        const onDragEnter = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            dragCounterRef.current += 1;
            setDragActive(true);
        };
        const onDragOver = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
        };
        const onDragLeave = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            dragCounterRef.current -= 1;
            if (dragCounterRef.current <= 0) {
                setDragActive(false);
                dragCounterRef.current = 0;
            }
        };
        const onDrop = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            setDragActive(false);
            dragCounterRef.current = 0;
            const files = (ev as any).dataTransfer?.files as FileList | undefined;
            stageOrUploadFiles(files || null);
        };
        zone.addEventListener("dragenter", onDragEnter as any);
        zone.addEventListener("dragover", onDragOver as any);
        zone.addEventListener("dragleave", onDragLeave as any);
        zone.addEventListener("drop", onDrop as any);
        return () => {
            zone.removeEventListener("dragenter", onDragEnter as any);
            zone.removeEventListener("dragover", onDragOver as any);
            zone.removeEventListener("dragleave", onDragLeave as any);
            zone.removeEventListener("drop", onDrop as any);
        };
    }, [stageOrUploadFiles]);

    // Auto-upload any staged files once a user is chosen
    useEffect(() => {
        if (selectedUser?.id && stagedFiles.length) {
            const pending = [...stagedFiles];
            setStagedFiles([]);
            void doUploadForFiles(pending);
        }
    }, [selectedUser?.id, stagedFiles, doUploadForFiles]);

    const toggleSelect = (id: string) => {
        setSelectedOutputIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const shareSelected = async () => {
        if (!selectedUser?.id) return;
        const ids = Array.from(selectedOutputIds);
        if (ids.length === 0) return;
        setShareError(null);
        setSharePending(true);
        try {
            await addOutputsToSharedBoard(String(selectedUser.id), ids);
            onClose();
        } catch (e: any) {
            setShareError(String(e?.message || e));
        } finally {
            setSharePending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative z-10 w-full max-w-5xl bg-white rounded-xl shadow-xl border p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-base font-semibold">Share videos</div>
                    <button onClick={onClose} className="h-8 w-8 rounded-full border flex items-center justify-center" aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <input
                                ref={inputRef}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search users by name or email…"
                                className="w-full border rounded-md px-3 py-2 text-sm"
                                aria-label="Search users"
                            />
                        </div>

                        <div className="border rounded-md">
                            {loadingUsers ? (
                                <div className="p-3 text-sm text-gray-600">Loading…</div>
                            ) : usersError ? (
                                <div className="p-3 text-sm text-red-600">{usersError}</div>
                            ) : users.length === 0 ? (
                                <div className="p-3 text-sm text-gray-500">No users</div>
                            ) : (
                                <ul className="max-h-64 overflow-y-auto divide-y">
                                    {users.map((u) => (
                                        <li
                                            key={u.id || u.email}
                                            className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${selectedUser?.id === u.id ? "bg-blue-50" : ""}`}
                                            onClick={() => onPickUser(u)}
                                        >
                                            <div className="text-sm font-medium">{u.full_name || u.email}</div>
                                            <div className="text-xs text-gray-600">{u.email}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {selectedUser && (
                            <div className="mt-3 text-xs text-gray-700">
                                Selected: <span className="font-medium">{selectedUser.full_name || selectedUser.email}</span>{" "}
                                <span className="text-gray-500">({selectedUser.email})</span>
                            </div>
                        )}
                    </div>

                    <div>
                        <div
                            ref={dropRef}
                            className={`border-2 border-dashed rounded-md p-4 mb-3 ${
                                dragActive ? "border-blue-600 bg-blue-50" : "border-gray-300"
                            }`}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm">
                                    <div className="font-medium">Upload or drag in videos</div>
                                    <div className="text-gray-600">MP4 or MOV</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".mp4,.mov"
                                        multiple
                                        className="hidden"
                                        onChange={onFileChange}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="px-3 py-2 text-sm border rounded-md"
                                        disabled={uploading}
                                    >
                                        {uploading ? "Uploading…" : "Choose files"}
                                    </button>
                                </div>
                            </div>
                            {uploadError && <div className="mt-2 text-sm text-red-600">{uploadError}</div>}
                            {!selectedUser && stagedFiles.length > 0 && (
                                <div className="mt-2 text-sm text-gray-700">{stagedFiles.length} video(s) staged. Select a user to upload.</div>
                            )}
                        </div>

                        {allVideos.length === 0 ? (
                            <div className="border rounded-md p-4 text-sm text-gray-500">No videos to share.</div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {allVideos.map((m) => {
                                    const isSelected = selectedOutputIds.has(m.id);
                                    return (
                                        <button
                                            type="button"
                                            key={`${m._origin || "gen"}-${m.id}`}
                                            onClick={() => toggleSelect(m.id)}
                                            className={`relative border rounded-md overflow-hidden bg-black focus:outline-none ${
                                                isSelected ? "ring-2 ring-blue-600" : ""
                                            }`}
                                            title={isSelected ? "Unselect" : "Select"}
                                        >
                                            <video src={m.permalink} muted playsInline autoPlay loop className="w-full h-40 object-cover" />
                                            <div
                                                className={`absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center text-xs ${
                                                    isSelected ? "bg-blue-600 text-white" : "bg-white text-gray-900 border"
                                                }`}
                                            >
                                                ✓
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                    {shareError ? <div className="text-sm text-red-600">{shareError}</div> : <span />}
                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="px-3 py-2 text-sm border rounded-md">
                            Close
                        </button>
                        <button
                            className="px-3 py-2 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
                            disabled={!selectedUser || selectedOutputIds.size === 0 || sharePending}
                            onClick={shareSelected}
                            title={!selectedUser ? "Select a user first" : selectedOutputIds.size === 0 ? "Select at least one video" : ""}
                        >
                            {sharePending ? "Sharing…" : "Share Selected"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareModal;
